import { FC, useCallback, useState } from 'react';
import { InfoDrawer } from '~/shared/InfoDrawer';
import { Text } from '~/shared/components';
import { useForm } from 'react-hook-form';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useRouter } from 'next/router';
import { useTranslation } from '~/shared/utils/translation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useValidationSchema } from './lib/useValidationSchema';
import { Form, Input } from '~/shared/components/form';
import { LoginInputs, Fields } from './models/fields';
import Grid from '~/shared/components/Grid/Grid';
import FacebookIcon from '~/icons/facebook.svg';
import GoogleIcon from '~/icons/google.svg';
import { notifyInfo } from '~/shared/utils/errorBoundary/toast';

import {
    Container,
    Content,
    StyledCTAButton,
    StyledSocialButton,
    StyledFooter,
    StyledLoginSignupWrapper,
    StyledLoginSignupButtonActive,
    StyledLoginSignupButtonInactive,
    StyledForgotPasswordLink,
    StyledLoginH4,
    StyledLoginTextblock,
} from './styled';
import { LoginDrawerMode } from './hooks/useLoginDrawerState';
import { useFrame } from '~/shared/utils';
import { usePage } from '~/templates/pages';
type LoginDrawerProps = {
    // open: boolean;
    open: boolean;
    close: () => void;
    loginDrawerMode: LoginDrawerMode;
    setLoginDrawerMode: (mode: LoginDrawerMode) => void;
};

export const LoginDrawer: FC<LoginDrawerProps> = ({
    open,
    close,
    loginDrawerMode,
    setLoginDrawerMode,
}) => {
    const {
        login,
        loginGoogle,
        loginFacebook,
        updatePassword,
        requestResetPassword,
        setPasswordFromToken,
        logout,
    } = useCustomer();
    const { translate } = useTranslation();
    const { push, query, replace } = useRouter();
    const { data: frame } = useFrame();
    const page = usePage();

    const [authenticationError, setAuthenticationError] = useState<string>('');
    const validationSchema = useValidationSchema();
    const useResetPasswordValidationSchema = useValidationSchema();
    const methods = useForm<LoginInputs>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: yupResolver(validationSchema),
    });
    const resetPasswordMethods = useForm<LoginInputs>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: yupResolver(useResetPasswordValidationSchema),
    });

    const toggleSetLoginDrawerMode = () => {
        if (loginDrawerMode === 'login') {
            setLoginDrawerMode('signup');
        } else {
            setLoginDrawerMode('login');
        }
    };

    const handleLoginSubmit = async (payload: LoginInputs) => {
        const request = {
            email: payload.email,
            password: payload.password,
        };
        try {
            await login(request);
            setAuthenticationError('');
            closeAndReset();
            if (query.basketid) {
                window.location.href = frame.settings?.pageReferences.basketPage?.url ?? '/';
            } else if (page.type === 'receiptPage') {
                window.location.href = '/';
            } else {
                window.location.reload();
            }
        } catch (e) {
            setAuthenticationError(translate('login.loginRejected'));
        }
    };

    const handleResetSendEmail = async (payload: LoginInputs) => {
        try {
            await requestResetPassword(payload.email);
            setLoginDrawerMode('forgotpasswordsubmitted');
        } catch (e) {
            setLoginDrawerMode('forgotpassworderror');
        } finally {
            reset();
        }
    };

    const handleResetPasswordFromToken = async (payload: LoginInputs) => {
        if (!query.resettoken) {
            closeAndReset();
            return;
        }
        try {
            await setPasswordFromToken(
                (Array.isArray(query.email) ? query.email[0] : query.email)?.replace('TA_', '') ??
                    '',
                query.resettoken as string,
                payload.newpassword,
            );
            setLoginDrawerMode('changepassworddone');
        } catch (e) {
            setLoginDrawerMode('changepasswordfail');
        } finally {
            reset();
            replace('/', undefined, { shallow: true });
        }
    };

    const setNewPassword = async (payload: LoginInputs) => {
        const request = {
            currentPassword: payload.password,
            password: payload.newpassword,
        };
        await updatePassword(request);
        await logout();
        notifyInfo(translate('login.changePasswordDone'));
        closeAndReset();
    };

    const reset = () => {
        methods.reset();
        resetPasswordMethods.reset();
    };

    const closeAndReset = useCallback(() => {
        reset();
        close();
    }, []);

    const goToSignup = () => {
        push(frame.settings?.pageReferences.createLoginPage?.url ?? '/');
        closeAndReset();
    };

    const facebookLogin = async () => {
        const url = await loginFacebook();
        if (url) {
            push(url);
            closeAndReset();
        } else throw new Error('Facebook login failed');
    };

    const googleLogin = async () => {
        const url = await loginGoogle();
        if (url) {
            push(url);
            closeAndReset();
        } else throw new Error('Google login failed');
    };

    const renderAuthenticationError = (errorMessage: string): JSX.Element => (
        <Text data-testid="auth-error" css={{ color: 'red' }} variant={'bodySmall'}>
            {errorMessage}
        </Text>
    );

    if (loginDrawerMode == 'login') {
        return (
            <InfoDrawer
                title={translate('login.loginBecomeMemberImercoplus')}
                open={open}
                close={closeAndReset}
            >
                <Content>
                    <StyledLoginSignupWrapper
                        onClick={toggleSetLoginDrawerMode}
                        tabIndex={0}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleSetLoginDrawerMode();
                            }
                        }}
                    >
                        <Grid flow="column" columns="1fr 1fr">
                            <StyledLoginSignupButtonActive>
                                {translate('login.login')}
                            </StyledLoginSignupButtonActive>

                            <StyledLoginSignupButtonInactive>
                                {translate('login.becomeMember')}
                            </StyledLoginSignupButtonInactive>
                        </Grid>
                    </StyledLoginSignupWrapper>
                    <Form methods={methods} onSubmit={handleLoginSubmit}>
                        <Container>
                            <Input
                                name={Fields.Email}
                                autoComplete="email"
                                label={translate('login.email')}
                                required
                            />
                            <Input
                                type="password"
                                name={Fields.Password}
                                autoComplete="current-password"
                                id="password"
                                label={translate('login.password')}
                                required
                                noValidation
                            />
                            <Grid flow="column" columns="1fr 1fr" columnGap="extrasmall">
                                <StyledForgotPasswordLink
                                    data-testid="forgot-password-link"
                                    onClick={() => setLoginDrawerMode('forgotpassword')}
                                >
                                    {translate('login.forgotPassword')}
                                </StyledForgotPasswordLink>
                            </Grid>

                            <StyledCTAButton data-testid="login" type="submit" variant="primary">
                                {translate('login.login')}
                            </StyledCTAButton>

                            {authenticationError && renderAuthenticationError(authenticationError)}
                        </Container>
                    </Form>

                    <Container>
                        <StyledSocialButton variant="plain" onClick={() => facebookLogin()}>
                            <FacebookIcon />
                            {translate('login.facebookLogin')}
                        </StyledSocialButton>
                        <StyledSocialButton variant="plain" onClick={() => googleLogin()}>
                            <GoogleIcon />
                            {translate('login.googleLogin')}
                        </StyledSocialButton>
                    </Container>
                    <StyledFooter></StyledFooter>
                </Content>
            </InfoDrawer>
        );
    } else if (loginDrawerMode == 'forgotpassword') {
        return (
            <InfoDrawer title={translate('login.forgotPassword')} open={open} close={closeAndReset}>
                <Form methods={methods} onSubmit={handleResetSendEmail}>
                    <Container>
                        <h4>{translate('login.needNewPassword')}</h4>
                        <br />
                        <Input
                            data-testid="email"
                            name={Fields.Email}
                            autoComplete="email"
                            label={translate('login.email')}
                            required
                        />
                        <StyledCTAButton
                            data-testid="reset-password-button"
                            type="submit"
                            variant="primary"
                        >
                            {translate('login.resetPassword')}
                        </StyledCTAButton>
                    </Container>
                </Form>
            </InfoDrawer>
        );
    } else if (loginDrawerMode == 'forgotpasswordsubmitted') {
        return (
            <InfoDrawer title={translate('login.forgotPassword')} open={open} close={closeAndReset}>
                <Container>
                    <h4 data-testid="forgot-password-submitted-headline">
                        {translate('login.needNewPassword')}
                    </h4>
                    <br />
                    {translate('login.youSubmittedWeWillSend')}
                </Container>
            </InfoDrawer>
        );
    } else if (loginDrawerMode == 'forgotpasswordsetnew') {
        return (
            <InfoDrawer title={translate('login.forgotPassword')} open={open} close={closeAndReset}>
                <Form methods={resetPasswordMethods} onSubmit={handleResetPasswordFromToken}>
                    <Container>
                        <h4>{translate('createAccount.changePassword')}</h4>
                        <Input
                            type="password"
                            name={Fields.NewPassword}
                            autoComplete="current-password"
                            id="password"
                            label={translate('login.password')}
                            required
                        />
                        <Input
                            type="password"
                            name={Fields.RepeatPassword}
                            autoComplete="current-password"
                            id="password"
                            label={translate('createAccount.repeatPassword')}
                            required
                        />

                        <StyledCTAButton type="submit" variant="primary">
                            {translate('createAccount.changePasswordDK')}
                        </StyledCTAButton>

                        {authenticationError && renderAuthenticationError(authenticationError)}
                    </Container>
                </Form>
            </InfoDrawer>
        );
    } else if (loginDrawerMode == 'forgotpassworderror') {
        return (
            <InfoDrawer title={translate('login.forgotPassword')} open={open} close={closeAndReset}>
                <StyledLoginH4>{translate('createAccount.changePassword')}</StyledLoginH4>
                <p>{translate('login.errorCreatingPassword')}</p>
                <StyledCTAButton type="button" onClick={closeAndReset} variant="primary">
                    {translate('login.close')}
                </StyledCTAButton>
            </InfoDrawer>
        );
    } else if (loginDrawerMode == 'changepassword') {
        return (
            <InfoDrawer
                title={translate('createAccount.changePasswordDK')}
                open={open}
                close={closeAndReset}
            >
                <Form methods={methods} onSubmit={setNewPassword}>
                    <Container>
                        <StyledLoginH4>{translate('createAccount.changePassword')}</StyledLoginH4>
                        <StyledLoginTextblock>
                            {translate('login.youCanChangeEasily')}
                        </StyledLoginTextblock>
                        <StyledLoginTextblock>
                            {translate('login.thePasswordShould')}
                        </StyledLoginTextblock>
                        <Input
                            data-testid="current-password"
                            type="password"
                            name={Fields.Password}
                            autoComplete="current-password"
                            id="password"
                            label={translate('login.currentPassword')}
                            required
                        />
                        <Input
                            data-testid="new-password"
                            type="password"
                            name={Fields.NewPassword}
                            autoComplete="new-password"
                            id="newpassword"
                            label={translate('login.newPassword')}
                            required
                        />
                        <Input
                            data-testid="confirm-password"
                            type="password"
                            name={Fields.RepeatPassword}
                            autoComplete="confirm-password"
                            id="repeatpassword"
                            label={translate('createAccount.repeatNewPassword')}
                            required
                        />

                        <StyledCTAButton
                            data-testid="change-password"
                            type="submit"
                            variant="primary"
                        >
                            {translate('createAccount.changePasswordDK')}
                        </StyledCTAButton>

                        {authenticationError && renderAuthenticationError(authenticationError)}
                    </Container>
                </Form>
            </InfoDrawer>
        );
    } else if (loginDrawerMode == 'changepasswordfail') {
        return (
            <InfoDrawer title={translate('login.forgotPassword')} open={open} close={closeAndReset}>
                {translate('login.changePasswordFail')}
                <StyledCTAButton
                    data-testid="change-password-fail"
                    type="button"
                    onClick={closeAndReset}
                    variant="primary"
                >
                    {translate('login.close')}
                </StyledCTAButton>
            </InfoDrawer>
        );
    } else if (loginDrawerMode == 'changepassworddone') {
        return (
            <InfoDrawer title={translate('login.forgotPassword')} open={open} close={closeAndReset}>
                {translate('login.changePasswordDone')}
                <StyledCTAButton
                    data-testid="change-password-done"
                    type="button"
                    onClick={closeAndReset}
                    variant="primary"
                >
                    {translate('login.close')}
                </StyledCTAButton>
            </InfoDrawer>
        );
    } else {
        // SIGN UP
        return (
            <InfoDrawer
                title={translate('login.loginBecomeMemberImercoplus')}
                open={open}
                close={closeAndReset}
            >
                <Content>
                    <StyledLoginSignupWrapper
                        onClick={toggleSetLoginDrawerMode}
                        tabIndex={0}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleSetLoginDrawerMode();
                            }
                        }}
                    >
                        <Grid flow="column" columns="1fr 1fr">
                            <StyledLoginSignupButtonInactive>
                                {translate('login.login')}
                            </StyledLoginSignupButtonInactive>

                            <StyledLoginSignupButtonActive>
                                {translate('login.becomeMember')}
                            </StyledLoginSignupButtonActive>
                        </Grid>
                    </StyledLoginSignupWrapper>
                    <Container>
                        <StyledSocialButton variant="plain" onClick={() => facebookLogin()}>
                            <FacebookIcon />
                            {translate('login.signUpFacebook')}
                        </StyledSocialButton>
                        <StyledSocialButton variant="plain" onClick={() => googleLogin()}>
                            <GoogleIcon />
                            {translate('login.signUpGoogle')}
                        </StyledSocialButton>
                        <StyledCTAButton type="button" onClick={goToSignup} variant="primary">
                            {translate('login.signUp')}
                        </StyledCTAButton>
                    </Container>
                    <StyledFooter></StyledFooter>
                </Content>
            </InfoDrawer>
        );
    }
};
